/**
 * Runtime test-id injection utility
 * Automatically injects descriptive test-ids into DOM elements
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { formatTestId } from "./pageDetector";

/**
 * Configuration for test-id injection
 */
export interface TestIdInjectionConfig {
  /**
   * Enable/disable automatic test-id injection
   * @default false (disabled by default)
   */
  enabled?: boolean;

  /**
   * Enable injection for section wrappers
   * @default true
   */
  injectWrappers?: boolean;

  /**
   * Enable injection for interactive elements (buttons, inputs, etc.)
   * @default true
   */
  injectInteractive?: boolean;

  /**
   * Enable injection for text components
   * @default true
   */
  injectText?: boolean;

  /**
   * Enable debug logging
   * @default false
   */
  debug?: boolean;

  /**
   * HTML attribute name to use for test-ids
   * @default 'data-testid'
   */
  attributeName?: string;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: Required<TestIdInjectionConfig> & {
  attributeName: string;
} = {
  enabled: false,
  injectWrappers: true,
  injectInteractive: true,
  injectText: true,
  debug: false,
  attributeName: "data-testId-automation",
};

/**
 * Generates a descriptive test-id based on element properties
 */
const generateDescriptiveId = (element: Element): string | null => {
  const tagName = element.tagName.toLowerCase();

  // Check for existing descriptive identifiers
  const existingId = element.id;
  const existingClass = element.className;
  const ariaLabel = element.getAttribute("aria-label");
  const placeholder = (element as HTMLInputElement).placeholder;
  const title = element.getAttribute("title");


  // Priority order for generating descriptive IDs
  if (ariaLabel) {
    return kebabCase(ariaLabel);
  }

  if (placeholder && tagName === "input") {
    return `input-${kebabCase(placeholder)}`;
  }

  if (title) {
    return kebabCase(title);
  }

  if (existingId && !new RegExp(/^\d+$/).exec(existingId)) {
    // Skip numeric IDs
    return kebabCase(existingId);
  }

  // Extract from class names (look for meaningful class names)
  if (existingClass && typeof existingClass === "string") {
    const meaningfulClasses = existingClass
      .split(" ")
      .filter((cls) => {
        // Filter out utility classes (short names, technical classes)
        return (
          cls.length > 3 &&
          !cls.startsWith("__") &&
          !cls.startsWith("_") &&
          !new RegExp(/^(mt|mb|ml|mr|pt|pb|pl|pr|p|m|flex|grid|text|bg|border|w|h)-/).exec(cls) &&
          !new RegExp(/^(display|justify|align|position|top|bottom|left|right|font|color)-/).exec(cls)
        );
      })
      .map(kebabCase);

    if (meaningfulClasses.length > 0) {
      return meaningfulClasses[0];
    }
  }

  // Tag-based generation as fallback
  return null;
};

/**
 * Converts a string to kebab-case
 */
const kebabCase = (str: string): string => {
  return str
    .toLowerCase()
    .replaceAll(/[^a-z0-9\s-]/g, "") // Remove special characters
    .replaceAll(/\s+/g, "-") // Replace spaces with hyphens
    .replaceAll(/-+/g, "-") // Replace multiple hyphens with single
    .replaceAll(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

/**
 * Finds the nearest container with a unique ID
 * @param element - The element to find the container for
 * @returns The container element or null
 */
const findNearestContainer = (element: Element): Element | null => {
  let parent = element.parentElement;
  let depth = 0;
  const maxDepth = 10; // Prevent infinite loops

  while (parent && depth < maxDepth) {
    const id = parent.id;

    // Check if it has a meaningful ID (not numeric)
    if (id && !new RegExp(/^\d+$/).exec(id)) {
      // Also check if it looks like a container (has container, section, wrapper in class or is a semantic element)
      const isContainer =
        parent.classList.contains("container") ||
        parent.classList.contains("section") ||
        parent.classList.contains("wrapper") ||
        parent.tagName.toLowerCase() === "section" ||
        parent.tagName.toLowerCase() === "article" ||
        parent.tagName.toLowerCase() === "main";

      if (isContainer) {
        return parent;
      }
    }

    // Special case: if current element has .container/.section/.wrapper class but no ID,
    // check its parent for an ID (handles structure like <div id="parent"><div class="container">)
    if (!id && parent.parentElement) {
      const grandParentId = parent.parentElement.id;
      if (
        grandParentId &&
        !new RegExp(/^\d+$/).exec(grandParentId) &&
        (parent.classList.contains("container") ||
          parent.classList.contains("section") ||
          parent.classList.contains("wrapper"))
      ) {
        return parent.parentElement;
      }
    }

    parent = parent.parentElement;
    depth++;
  }

  return null;
};

/**
 * Gets a container suffix for the test-id
 * @param element - The element to find the container for
 * @returns Container suffix (e.g., "-learn-more") or empty string
 */
const getContainerSuffix = (element: Element): string => {
  const container = findNearestContainer(element);
  if (container?.id) {
    return `-${kebabCase(container.id)}`;
  }
  return "";
};

/**
 * Generates a test-id for a section wrapper
 */
const generateWrapperTestId = (element: Element, index: number): string => {
  const existingId = generateDescriptiveId(element);
  if (existingId) {
    return `section-${existingId}`;
  }

  const classList = Array.from(element.classList);
  const meaningfulClass = classList.find(
    (cls) => cls.length > 4 && !cls.startsWith("__")
  );

  if (meaningfulClass) {
    return `section-${kebabCase(meaningfulClass)}`;
  }

  return `section-${index}`;
};

/**
 * Generates a test-id for interactive elements
 */
const generateInteractiveTestId = (element: Element, index: number): string => {
  const tagName = element.tagName.toLowerCase();
  const type = (element as HTMLInputElement).type;
  const existingId = generateDescriptiveId(element);

  if (existingId) {
    return `${existingId}-${tagName}`;
  }

  // Button-specific logic - use first 2 words of button text
  if (tagName === "button") {
    const buttonText = element.textContent?.trim();
    if (buttonText) {
      const firstTwoWords = buttonText
        .split(/\s+/)
        .slice(0, 2)
        .join("-")
        .substring(0, 30);
      return `${kebabCase(firstTwoWords)}-button`;
    }
    return `button-${index}`;
  }

  // Input-specific logic
  if (tagName === "input") {
    const inputType = type || "text";
    const placeholder = (element as HTMLInputElement).placeholder;
    const ariaLabel = element.getAttribute("aria-label");

    // Use aria-label, placeholder, or type
    if (ariaLabel) {
      return `${kebabCase(ariaLabel)}-${tagName}`;
    }
    if (placeholder) {
      const firstTwoWords = placeholder
        .split(/\s+/)
        .slice(0, 2)
        .join("-")
        .substring(0, 30);
      return `${kebabCase(firstTwoWords)}-${tagName}`;
    }
    return `${inputType}-${tagName}`;
  }

  // Link-specific logic - use first 2 words of link text
  if (tagName === "a") {
    const linkText = element.textContent?.trim();
    if (linkText) {
      const firstTwoWords = linkText
        .split(/\s+/)
        .slice(0, 2)
        .join("-")
        .substring(0, 30);
      return `${kebabCase(firstTwoWords)}-link`;
    }
    return `link-${index}`;
  }

  // Select-specific logic
  if (tagName === "select") {
    const ariaLabel = element.getAttribute("aria-label");
    if (ariaLabel) {
      return `${kebabCase(ariaLabel)}-select`;
    }
    return `select-${index}`;
  }

  // Checkbox/Radio-specific logic
  if (tagName === "input" && (type === "checkbox" || type === "radio")) {
    const label = element.getAttribute("aria-label") || "";
    if (label) {
      return `${kebabCase(label)}-${type}`;
    }
    return `${type}-${index}`;
  }

  // Textarea-specific logic
  if (tagName === "textarea") {
    const placeholder = (element as HTMLTextAreaElement).placeholder;
    if (placeholder) {
      const firstTwoWords = placeholder
        .split(/\s+/)
        .slice(0, 2)
        .join("-")
        .substring(0, 30);
      return `${kebabCase(firstTwoWords)}-${tagName}`;
    }
    return `${tagName}-${index}`;
  }

  return `${tagName}-${index}`;
};

/**
 * Generates a test-id for text components
 */
const generateTextTestId = (element: Element, index: number): string => {
  const tagName = element.tagName.toLowerCase();

  // ⛔ DO NOT use generateDescriptiveId yet

  // Typography first — text has highest priority
  if (element.classList.contains("typography")) {
    const text = element.textContent?.trim();

    if (text) {
      const firstTwoWords = text
        .split(/\s+/)
        .slice(0, 2)
        .join("-")
        .substring(0, 30);

      return `${kebabCase(firstTwoWords)}-text`;
    }

    return `typography-text`;
  }

  // Headings / paragraphs
  if (["h1", "h2", "h3", "h4", "h5", "h6", "p", "span"].includes(tagName)) {
    const text = element.textContent?.trim();
    if (text) {
      const firstTwoWords = text.split(/\s+/).slice(0, 2).join("-");
      return `${kebabCase(firstTwoWords)}-${tagName}`;
    }
  }

  // ✅ Only now allow descriptive fallback
  const existingId = generateDescriptiveId(element);
  if (existingId) {
    return `${existingId}-text`;
  }

  return `${tagName}-${index}`;
};

/**
 * Injects test-ids into section wrappers
 */
const injectWrapperTestIds = (
  pathname: string,
  config: Required<TestIdInjectionConfig> & { attributeName?: string }
): number => {
  const attributeName = config.attributeName || "data-testId-automation";
  let count = 0;
  const processedElements = new WeakSet<Element>();
  const usedTestIds = new Set<string>();

  // Select potential wrapper elements
  const selectors = [
    `div[id]:not([${attributeName}])`,
    `section[id]:not([${attributeName}])`,
    `article[id]:not([${attributeName}])`,
    `main[id]:not([${attributeName}])`,
    `div[class*='section']:not([${attributeName}]):not([id])`,
    `div[class*='container']:not([${attributeName}]):not([id])`,
    `div[class*='wrapper']:not([${attributeName}]):not([id])`,
    `section:not([${attributeName}]):not([id])`,
    `article:not([${attributeName}]):not([id])`,
  ];

  const wrappers = document.querySelectorAll(selectors.join(", "));

  wrappers.forEach((element) => {
    if (processedElements.has(element)) return;

    // Skip if already has a test-id
    if (element.hasAttribute(attributeName)) {
      processedElements.add(element);
      return;
    }

    // Generate base test-id
    let descriptiveId = generateWrapperTestId(element, count);

    // If the element has an ID, use it as the container name
    if (element.id && !new RegExp(/^\d+$/).exec(element.id)) {
      descriptiveId = `container-${kebabCase(element.id)}`;
    }

    let testId = formatTestId(pathname, descriptiveId);

    // Ensure uniqueness by appending index if needed
    let uniqueTestId = testId;
    let index = 0;
    while (usedTestIds.has(uniqueTestId)) {
      uniqueTestId = `${testId}-${index}`;
      index++;
    }

    usedTestIds.add(uniqueTestId);
    element.setAttribute(attributeName, uniqueTestId);
    debugLogConfig(`Injected wrapper test-id: ${uniqueTestId}`, config, element);
    processedElements.add(element);
    count++;
  });

  return count;
};

/**
 * Injects test-ids into interactive elements
 */
const injectInteractiveTestIds = (
  pathname: string,
  config: Required<TestIdInjectionConfig> & { attributeName?: string }
): number => {
  let count = 0;
  const usedTestIds = new Set<string>();
  const attributeName = config.attributeName || "data-testId-automation";

  const interactiveSelectors = [
    `button:not([${attributeName}])`,
    `input:not([${attributeName}])`,
    `a[href]:not([${attributeName}])`,
    `select:not([${attributeName}])`,
    `textarea:not([${attributeName}])`,
    `[role='button']:not([${attributeName}])`,
    `[role='link']:not([${attributeName}])`,
    `[role='checkbox']:not([${attributeName}])`,
    `[role='radio']:not([${attributeName}])`,
  ];

  const interactiveElements = document.querySelectorAll(
    interactiveSelectors.join(", ")
  );

  interactiveElements.forEach((element) => {
    // Skip if already has a test-id (selector should handle this, but double-check)
    if (element.hasAttribute(attributeName)) {
      return;
    }

    // Generate base test-id
    const descriptiveId = generateInteractiveTestId(element, count);
    const containerSuffix = getContainerSuffix(element);

    // Combine page, container, and element description
    let testId = formatTestId(pathname, `${containerSuffix}-${descriptiveId}`);

    // Ensure uniqueness by appending index if needed
    let uniqueTestId = testId;
    let index = 0;
    while (usedTestIds.has(uniqueTestId)) {
      uniqueTestId = `${testId}-${index}`;
      index++;
    }

    usedTestIds.add(uniqueTestId);
    element.setAttribute(attributeName, uniqueTestId);
    debugLogConfig(`Injected interactive test-id: ${uniqueTestId}`, config, element);
    count++;
  });

  return count;
};

/**
 * Injects test-ids into text components
 */
const injectTextTestIds = (
  pathname: string,
  config: Required<TestIdInjectionConfig> & { attributeName?: string }
): number => {
  let count = 0;
  const usedTestIds = new Set<string>();
  const attributeName = config.attributeName || "data-testId-automation";

  const textSelectors = [
    `h1:not([${attributeName}])`,
    `h2:not([${attributeName}])`,
    `h3:not([${attributeName}])`,
    `h4:not([${attributeName}])`,
    `h5:not([${attributeName}])`,
    `h6:not([${attributeName}])`,
    `p:not([${attributeName}])`,
    `span:not([${attributeName}]):not([class*='icon'])`,
    `label:not([${attributeName}])`,
    // Custom Typography component (renders as div/span/etc with .typography class)
    `.typography:not([${attributeName}])`,
  ];

  const textElements = document.querySelectorAll(textSelectors.join(", "));

  textElements.forEach((element) => {
    // Skip if empty text or only whitespace
    const text = element.textContent?.trim();
    if (!text) {
      return;
    }

    // Skip if already has a test-id
    if (element.hasAttribute(attributeName)) {
      return;
    }

    // Generate base test-id
    const descriptiveId = generateTextTestId(element, count);
    const containerSuffix = getContainerSuffix(element);

    // Combine page, container, and element description
    let testId = formatTestId(pathname, `${containerSuffix}-${descriptiveId}`);

    // Ensure uniqueness by appending index if needed
    let uniqueTestId = testId;
    let index = 0;
    while (usedTestIds.has(uniqueTestId)) {
      uniqueTestId = `${testId}-${index}`;
      index++;
    }

    usedTestIds.add(uniqueTestId);
    element.setAttribute(attributeName, uniqueTestId);
    debugLogConfig(`Injected text test-id: ${uniqueTestId}`, config, element);
    count++;
  });

  return count;
};

/**
 * Logs debug messages if debug mode is enabled
 */
const debugLog = (message: string, ...args: any[]) => {
  if (process.env.REACT_APP_TEST_ID_DEBUG === "true") {
    console.log(`[TestIdInjector] ${message}`, ...args);
  }
};

/**
 * Logs debug messages for a specific config
 */
const debugLogConfig = (message: string, config: Required<TestIdInjectionConfig>, ...args: any[]) => {
  if (config.debug || process.env.REACT_APP_TEST_ID_DEBUG === "true") {
    console.log(`[TestIdInjector] ${message}`, ...args);
  }
};

/**
 * Main injection function
 */
const injectTestIds = (
  pathname: string,
  config: Required<TestIdInjectionConfig>
) => {
  debugLogConfig(`Starting test-id injection for path: ${pathname}`, config);
  debugLogConfig("Configuration:", config);

  const results = {
    wrappers: 0,
    interactive: 0,
    text: 0,
  };

  // Inject wrappers first so containers are identified
  if (config.injectWrappers) {
    results.wrappers = injectWrapperTestIds(pathname, config);
  }

  // Then inject interactive and text elements with container awareness
  if (config.injectInteractive) {
    results.interactive = injectInteractiveTestIds(pathname, config);
  }

  if (config.injectText) {
    results.text = injectTextTestIds(pathname, config);
  }

  const total = results.wrappers + results.interactive + results.text;
  debugLogConfig(`Injection complete. Total test-ids added: ${total}`, config, results);

  return results;
};

/**
 * Custom hook to enable runtime test-id injection
 * @param config - Configuration options
 * @returns Injection results object
 */
export const useTestIdInjection = (config: TestIdInjectionConfig = {}) => {
  const location = useLocation();
  const isEnabled =
    config.enabled ?? process.env.REACT_APP_AUTO_TEST_ID === "true";
  const mergedConfig = { ...DEFAULT_CONFIG, ...config, enabled: isEnabled };
  const isInjected = useRef(false);

  useEffect(() => {
    if (!mergedConfig.enabled) {
      debugLog("Test-id injection is disabled");
      return;
    }

    // Use MutationObserver to handle dynamically added elements
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldInject = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          shouldInject = true;
        }
      });

      if (shouldInject) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          injectTestIds(location.pathname, mergedConfig);
        }, 100);
      }
    });

    isInjected.current = true;

    // Start observing the document
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    debugLog("MutationObserver started for dynamic element injection");

    // Cleanup
    return () => {
      mutationObserver.disconnect();
      debugLog("MutationObserver stopped");
    };
  }, [location.pathname, mergedConfig.enabled]);

  // Re-inject on route changes
  useEffect(() => {
    if (mergedConfig.enabled && isInjected.current) {
      debugLog(`Route changed to: ${location.pathname}`);
      // Small delay to ensure new route content is rendered
      const timeout = setTimeout(() => {
        injectTestIds(location.pathname, mergedConfig);
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [location.pathname]);

  return {
    enabled: mergedConfig.enabled,
    pathname: location.pathname,
  };
};

/**
 * Clear all injected test-ids with a specific attribute name
 * @param attributeName - The attribute name to clear (default: data-testId-automation)
 */
export const clearTestIds = (attr = "data-testId-automation") => {
  const elements = document.querySelectorAll(`[${attr}]`);
  elements.forEach((el) => el.removeAttribute(attr));
  debugLog(`Cleared ${elements.length} test-ids with attribute: ${attr}`);
  return elements.length;
};

/**
 * Manual injection function (can be called from console for debugging)
 * Supports two calling styles:
 * 1. Simple: window.injectTestIds("data-testId-automation")
 * 2. Full config: window.injectTestIds({ attributeName: "data-testId-automation", debug: true })
 */
export const manualInjectTestIds = (
  configOrAttributeName?: TestIdInjectionConfig | string
) => {
  const pathname = globalThis.location.pathname;
  let mergedConfig: Required<TestIdInjectionConfig> & {
    attributeName?: string;
  };

  // Support simple string parameter (attribute name)
  if (typeof configOrAttributeName === "string") {
    mergedConfig = {
      ...DEFAULT_CONFIG,
      attributeName: configOrAttributeName,
    };
  } else {
    // Support full config object
    mergedConfig = { ...DEFAULT_CONFIG, ...configOrAttributeName };
  }

  // Inject immediately (no delay)
  const results = injectTestIds(pathname, mergedConfig);
  return results;
};

// Make manual injection available globally for debugging
if (globalThis.window !== undefined) {
  (globalThis as any).injectTestIds = manualInjectTestIds;
  (globalThis as any).clearTestIds = clearTestIds;
  debugLog("Manual injection function available at window.injectTestIds()");
  debugLog("Clear test-ids: window.clearTestIds()");
  debugLog("Usage: window.injectTestIds('data-testId-automation')");
}
